import { useEffect, useCallback, useRef } from 'react';

const DRAFT_STORAGE_KEY = 'esign_document_draft';
const AUTO_SAVE_INTERVAL = 30000; // 30초

/**
 * 문서 작성 중 자동 임시저장 커스텀 훅
 * - 30초 간격으로 로컬 스토리지에 자동 저장
 * - 페이지 새로고침/이탈 시 데이터 복구 가능
 * - 상신 성공 시 임시저장 데이터 삭제
 *
 * @param {Object} params - 임시저장 파라미터
 * @param {Object} params.formInfo - 양식 정보
 * @param {Object} params.documentInfo - 문서 정보
 * @param {string} params.editorContent - 에디터 내용
 * @param {Array} params.referenceDocuments - 참조문서 목록
 * @param {Array} params.attachments - 첨부파일 목록 (파일 객체 제외)
 * @param {boolean} params.isEnabled - 자동 저장 활성화 여부
 * @returns {Object} 임시저장 관련 함수들
 */
function useAutoSaveDraft({
  formInfo,
  documentInfo,
  editorContent,
  referenceDocuments,
  attachments = [],
  isEnabled = true,
}) {
  const lastSavedRef = useRef(null);

  /**
   * 현재 문서 데이터를 로컬 스토리지에 저장
   */
  const saveDraft = useCallback(
    function () {
      if (!isEnabled) return;

      // 저장할 데이터가 있는지 확인
      if (!formInfo?.formId && !documentInfo?.subject && !editorContent) {
        return;
      }

      const draft = {
        formInfo: {
          formId: formInfo?.formId,
          formName: formInfo?.formName,
        },
        documentInfo: {
          subject: documentInfo?.subject || '',
          approvalLine: documentInfo?.approvalLine || [],
          recipients: documentInfo?.recipients || [],
          executors: documentInfo?.executors || [],
        },
        editorContent: editorContent || '',
        referenceDocuments: referenceDocuments || [],
        // 첨부파일은 파일 객체를 저장할 수 없으므로 메타데이터만 저장
        attachmentMeta: attachments.map(function (att) {
          return {
            name: att.file?.name || att.name,
            size: att.file?.size || att.size,
          };
        }),
        savedAt: new Date().toISOString(),
      };

      try {
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
        lastSavedRef.current = draft.savedAt;
      } catch (error) {
        console.error('임시저장 실패:', error);
      }
    },
    [formInfo, documentInfo, editorContent, referenceDocuments, attachments, isEnabled],
  );

  /**
   * 로컬 스토리지에서 임시저장 데이터 불러오기
   */
  const loadDraft = useCallback(function () {
    try {
      const draftJson = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!draftJson) return null;

      const draft = JSON.parse(draftJson);
      return draft;
    } catch (error) {
      console.error('임시저장 불러오기 실패:', error);
      return null;
    }
  }, []);

  /**
   * 로컬 스토리지에서 임시저장 데이터 삭제
   */
  const clearDraft = useCallback(function () {
    try {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      lastSavedRef.current = null;
    } catch (error) {
      console.error('임시저장 삭제 실패:', error);
    }
  }, []);

  /**
   * 임시저장 데이터 존재 여부 확인
   */
  const hasDraft = useCallback(function () {
    try {
      return !!localStorage.getItem(DRAFT_STORAGE_KEY);
    } catch (error) {
      return false;
    }
  }, []);

  /**
   * 임시저장 데이터의 저장 시간 가져오기
   */
  const getDraftSavedTime = useCallback(function () {
    try {
      const draftJson = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!draftJson) return null;

      const draft = JSON.parse(draftJson);
      return draft.savedAt ? new Date(draft.savedAt) : null;
    } catch (error) {
      return null;
    }
  }, []);

  // 자동 저장 인터벌 설정
  useEffect(
    function () {
      if (!isEnabled) return;

      const intervalId = setInterval(function () {
        saveDraft();
      }, AUTO_SAVE_INTERVAL);

      return function () {
        clearInterval(intervalId);
      };
    },
    [saveDraft, isEnabled],
  );

  // 페이지 이탈 전 저장
  useEffect(
    function () {
      if (!isEnabled) return;

      function handleBeforeUnload() {
        saveDraft();
      }

      window.addEventListener('beforeunload', handleBeforeUnload);

      return function () {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    },
    [saveDraft, isEnabled],
  );

  return {
    saveDraft,
    loadDraft,
    clearDraft,
    hasDraft,
    getDraftSavedTime,
    lastSavedAt: lastSavedRef.current,
  };
}

export default useAutoSaveDraft;
